'use client';

import { LocaleLink } from '@/core/i18n';
import { ArrowRight, Shield, CheckCircle2 } from 'lucide-react';
import { cn } from '@/core/utils/cn';
import { usePageContent } from '@/core/i18n/content';
import { TEMPLATE_CHROME } from '@/core/i18n/content/template-chrome.content';
import type { TemplateChromeContent } from '@/core/i18n/content/template-chrome.types';
import { COMPLIANCE_HUB_CONTENT } from '@/core/i18n/content/compliance-hub.content';
import type { ComplianceHubContent } from '@/core/i18n/content/compliance-hub.content';
import { useTranslation } from '@/core/i18n';

/* -------------------------------------------------------------------------- */
/*  Data                                                                      */
/* -------------------------------------------------------------------------- */

interface RegionMeta {
  slug: string;
  flag: string;
}

const REGION_META: RegionMeta[] = [
  { slug: 'eu-uk', flag: 'EU' },
  { slug: 'united-states', flag: 'US' },
  { slug: 'uae-middle-east', flag: 'AE' },
  { slug: 'thailand-sea', flag: 'TH' },
  { slug: 'australia', flag: 'AU' },
  { slug: 'japan', flag: 'JP' },
  { slug: 'india', flag: 'IN' },
];

const FLAG_COLORS: Record<string, string> = {
  EU: 'bg-hms-blue/15 text-hms-blue border-hms-blue/20',
  US: 'bg-hms-red/15 text-hms-red border-hms-red/20',
  AE: 'bg-hms-green/15 text-hms-green border-hms-green/20',
  TH: 'bg-hms-gold/15 text-hms-gold border-hms-gold/20',
  AU: 'bg-hms-teal/15 text-hms-teal border-hms-teal/20',
  JP: 'bg-hms-red/15 text-hms-red border-hms-red/20',
  IN: 'bg-hms-gold/15 text-hms-gold border-hms-gold/20',
};

/* -------------------------------------------------------------------------- */
/*  Hero                                                                      */
/* -------------------------------------------------------------------------- */

function Hero({ c }: { c: ComplianceHubContent }) {
  const { t } = useTranslation();
  return (
    <section id="compliance-hub-hero" className="relative w-full overflow-hidden bg-hms-bg">
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: 'radial-gradient(ellipse 80% 60% at 50% 30%, rgba(200,145,58,0.08) 0%, transparent 70%)' }}
      />
      <div className="relative mx-auto max-w-4xl px-4 pb-16 pt-24 text-center sm:px-6 sm:pt-32 lg:px-8 lg:pt-40">
        <span className="mb-4 inline-flex items-center gap-2 rounded-full border border-hms-gold/20 bg-hms-gold/5 px-4 py-1.5 text-xs font-semibold tracking-wide text-hms-gold">
          <Shield className="h-3.5 w-3.5" />
          {c.heroBadge}
        </span>
        <h1
          id="compliance-hub-title"
          className={cn(
            'font-poppins text-4xl font-extrabold leading-tight tracking-tight sm:text-5xl lg:text-6xl',
            'bg-gradient-to-r from-hms-gold via-hms-goldLt to-amber-400 bg-clip-text text-transparent',
          )}
        >
          {c.heroTitle}
        </h1>
        <p className="mx-auto mt-6 max-w-2xl text-base text-hms-textSub sm:text-lg">
          {c.heroSubtitle}
        </p>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Region Card                                                               */
/* -------------------------------------------------------------------------- */

function RegionCard({ meta, c, t }: { meta: RegionMeta; c: ComplianceHubContent; t: TemplateChromeContent }) {
  const regionContent = c.regions[meta.slug];
  return (
    <LocaleLink
      id={`compliance-hub-card-${meta.slug}`}
      href={`/compliance/${meta.slug}`}
      className={cn(
        'group flex flex-col rounded-xl border border-hms-border bg-hms-card p-6',
        'transition-all duration-200 hover:border-hms-gold/30 hover:shadow-lg hover:shadow-hms-gold/5',
      )}
    >
      <div className="mb-5 flex items-center gap-4">
        <div
          className={cn(
            'flex h-12 w-12 shrink-0 items-center justify-center rounded-full border text-sm font-bold',
            FLAG_COLORS[meta.flag] ?? 'bg-hms-surface text-hms-text border-hms-border',
          )}
        >
          {meta.flag}
        </div>
        <h3
          id={`compliance-hub-name-${meta.slug}`}
          className="text-lg font-semibold text-hms-text"
        >
          {regionContent.region}
        </h3>
      </div>

      <ul className="mb-5 flex-1 space-y-2.5">
        {regionContent.items.map((item, idx) => (
          <li
            key={idx}
            className="flex items-start gap-2.5 text-sm leading-relaxed text-hms-textSub"
          >
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-hms-green" />
            <span>{item}</span>
          </li>
        ))}
      </ul>

      <span className="inline-flex items-center gap-1.5 text-sm font-medium text-hms-gold transition-colors group-hover:text-hms-goldLt">
        {t.common.viewDetails}
        <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
      </span>
    </LocaleLink>
  );
}

/* -------------------------------------------------------------------------- */
/*  Main Component                                                            */
/* -------------------------------------------------------------------------- */

export function ComplianceHub() {
  const t = usePageContent(TEMPLATE_CHROME);
  const c = usePageContent(COMPLIANCE_HUB_CONTENT);
  return (
    <div id="compliance-hub-root" className="min-h-screen bg-hms-bg">
      <Hero c={c} />

      {/* Region Grid */}
      <section className="mx-auto max-w-6xl px-4 pb-12 sm:px-6 lg:px-8">
        <div
          id="compliance-hub-grid"
          className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3"
        >
          {REGION_META.map((meta) => (
            <RegionCard key={meta.slug} meta={meta} c={c} t={t} />
          ))}
        </div>
      </section>

      {/* CTA */}
      <section
        id="compliance-hub-cta"
        className="mx-auto max-w-4xl px-4 pb-24 pt-8 text-center sm:px-6 lg:px-8"
      >
        <div className="rounded-xl border border-hms-border bg-hms-card p-8 sm:p-12">
          <h2 className="text-2xl font-bold text-hms-text sm:text-3xl">
            {c.ctaTitle}
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-sm text-hms-textSub sm:text-base">
            {c.ctaSubtitle}
          </p>
          <LocaleLink
            id="compliance-hub-cta-contact"
            href="/contact"
            className={cn(
              'mt-6 inline-flex items-center gap-2 rounded-lg bg-hms-gold px-8 py-3.5',
              'text-sm font-semibold tracking-wide text-hms-bg shadow-lg shadow-hms-gold/20',
              'transition-all duration-200 hover:bg-hms-goldLt hover:shadow-hms-gold/30 active:scale-[0.98]',
            )}
          >
            {t.cta.contactBtn}
            <ArrowRight className="h-4 w-4" />
          </LocaleLink>
        </div>
      </section>
    </div>
  );
}
